import { create } from 'zustand'

/**
 * pipelineStore — holds the live pipeline strip + Live Dashboard state
 * (plan §9.12, §M4.3).
 *
 * WebSocket events from /projects/{id}/events feed into this store via
 * useProjectEvents, which drives the PipelineStrip, PhaseBriefingPanel,
 * GateReviewPanel, and LiveDashboard components.
 */
export type PhaseStatus =
  | 'pending'
  | 'briefing'
  | 'running'
  | 'gate'
  | 'done'
  | 'skipped'
  | 'rewound'

export type AgentStatus = 'pending' | 'running' | 'done' | 'error'

export interface AgentState {
  status: AgentStatus
  inputTokens: number
  outputTokens: number
  files: string[]
  startedAt: number | null
  completedAt: number | null
  errorMessage?: string
}

export interface RqsBreakdown {
  ears_conformance: number
  incose_40: number
  traceability: number
  provenance: number
}

export interface RqsScoreSnapshot {
  overall: number
  breakdown: RqsBreakdown
  audience?: string
  rewriteCount: number
}

import type { RunStateRecord } from '@/types'

export interface PipelineState {
  projectId: string | null
  runId: number | null
  phaseStatuses: Record<1 | 2 | 3 | 4 | 5, PhaseStatus>
  tokensUsed: number
  tokensBudget: number
  currentGate: 1 | 2 | 3 | 4 | 5 | null
  currentBriefing: 1 | 2 | 3 | 4 | 5 | null
  agents: Record<string, AgentState>
  rqs: RqsScoreSnapshot | null
  setProjectRun: (projectId: string, runId: number) => void
  setPhaseStatus: (phase: 1 | 2 | 3 | 4 | 5, status: PhaseStatus) => void
  setTokens: (used: number, budget?: number) => void
  setCurrentGate: (gate: 1 | 2 | 3 | 4 | 5 | null) => void
  setCurrentBriefing: (phase: 1 | 2 | 3 | 4 | 5 | null) => void
  agentStarted: (agent: string) => void
  agentCompleted: (
    agent: string,
    payload: {
      inputTokens?: number
      outputTokens?: number
      files?: string[]
    },
  ) => void
  setRqs: (snapshot: RqsScoreSnapshot) => void
  /**
   * Hydrate the in-memory pipeline store from a persisted ``run-state.json``
   * record (resume-after-reboot — see brain/14). The Command Center calls
   * this on mount before the WebSocket reconnects so the user sees their
   * real token meter, per-agent stats, and RQS breakdown immediately
   * instead of an empty pipeline strip.
   */
  hydrateFromRunState: (state: RunStateRecord) => void
  reset: () => void
}

const EMPTY_PHASES: Record<1 | 2 | 3 | 4 | 5, PhaseStatus> = {
  1: 'pending',
  2: 'pending',
  3: 'pending',
  4: 'pending',
  5: 'pending',
}

const EMPTY_AGENTS: Record<string, AgentState> = {}

export const usePipelineStore = create<PipelineState>((set) => ({
  projectId: null,
  runId: null,
  phaseStatuses: { ...EMPTY_PHASES },
  tokensUsed: 0,
  tokensBudget: 500_000,
  currentGate: null,
  currentBriefing: null,
  agents: { ...EMPTY_AGENTS },
  rqs: null,

  setProjectRun: (projectId, runId) =>
    set({
      projectId,
      runId,
      phaseStatuses: { ...EMPTY_PHASES },
      agents: { ...EMPTY_AGENTS },
      rqs: null,
    }),

  setPhaseStatus: (phase, status) =>
    set((state) => ({
      phaseStatuses: { ...state.phaseStatuses, [phase]: status },
    })),

  setTokens: (used, budget) =>
    set((state) => ({
      tokensUsed: used,
      tokensBudget: budget ?? state.tokensBudget,
    })),

  setCurrentGate: (gate) => set({ currentGate: gate }),
  setCurrentBriefing: (phase) => set({ currentBriefing: phase }),

  agentStarted: (agent) =>
    set((state) => ({
      agents: {
        ...state.agents,
        [agent]: {
          status: 'running',
          inputTokens: state.agents[agent]?.inputTokens ?? 0,
          outputTokens: state.agents[agent]?.outputTokens ?? 0,
          files: state.agents[agent]?.files ?? [],
          startedAt: Date.now(),
          completedAt: null,
        },
      },
    })),

  agentCompleted: (agent, payload) =>
    set((state) => ({
      agents: {
        ...state.agents,
        [agent]: {
          status: 'done',
          inputTokens: payload.inputTokens ?? state.agents[agent]?.inputTokens ?? 0,
          outputTokens:
            payload.outputTokens ?? state.agents[agent]?.outputTokens ?? 0,
          files: payload.files ?? state.agents[agent]?.files ?? [],
          startedAt: state.agents[agent]?.startedAt ?? Date.now(),
          completedAt: Date.now(),
        },
      },
    })),

  setRqs: (snapshot) => set({ rqs: snapshot }),

  hydrateFromRunState: (state) =>
    set((current) => {
      // Translate the persisted phase-status enum (subset of the live
      // PhaseStatus union) into the in-memory enum. Unknown values
      // fall back to "pending" so the runner can correct on next event.
      const PHASE_STATUS_MAP: Record<string, PhaseStatus> = {
        pending: 'pending',
        running: 'running',
        done: 'done',
        error: 'pending',
      }
      const nextPhases: Record<1 | 2 | 3 | 4 | 5, PhaseStatus> = {
        ...current.phaseStatuses,
      }
      for (const key of ['1', '2', '3', '4', '5'] as const) {
        const summary = state.phases[key]
        if (!summary) continue
        const idx = Number(key) as 1 | 2 | 3 | 4 | 5
        // Don't overwrite a "more advanced" status the live socket has
        // already applied (e.g. server emitted "gate" before we hydrated).
        const live = current.phaseStatuses[idx]
        if (live === 'gate' || live === 'briefing') continue
        nextPhases[idx] = PHASE_STATUS_MAP[summary.status] ?? 'pending'
      }

      // Per-agent map → AgentState. We preserve the running flag from
      // the persisted record so an in-flight phase shows the spinner
      // even when the server is still booting (it'll be corrected once
      // the WebSocket reconnects).
      const nextAgents: Record<string, AgentState> = { ...current.agents }
      for (const [name, rec] of Object.entries(state.agents)) {
        const startedMs = rec.started_at
          ? Date.parse(rec.started_at)
          : null
        const completedMs = rec.completed_at
          ? Date.parse(rec.completed_at)
          : null
        nextAgents[name] = {
          status: rec.status,
          inputTokens: rec.input_tokens,
          outputTokens: rec.output_tokens,
          files: [...rec.files],
          startedAt: Number.isFinite(startedMs) ? startedMs : null,
          completedAt: Number.isFinite(completedMs) ? completedMs : null,
          errorMessage: rec.error_message ?? undefined,
        }
      }

      const nextRqs: RqsScoreSnapshot | null = state.rqs
        ? {
            overall: state.rqs.overall,
            breakdown: { ...state.rqs.breakdown },
            audience: state.rqs.audience ?? undefined,
            rewriteCount: state.rqs.rewrite_count,
          }
        : current.rqs

      return {
        // Don't shrink the cumulative token total — if a live event
        // has already raised it, keep the higher value.
        tokensUsed: Math.max(current.tokensUsed, state.tokens_used || 0),
        tokensBudget: state.tokens_budget || current.tokensBudget,
        phaseStatuses: nextPhases,
        agents: nextAgents,
        rqs: nextRqs,
      }
    }),

  reset: () =>
    set({
      projectId: null,
      runId: null,
      phaseStatuses: { ...EMPTY_PHASES },
      tokensUsed: 0,
      currentGate: null,
      currentBriefing: null,
      agents: { ...EMPTY_AGENTS },
      rqs: null,
    }),
}))
